import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { mdiAccountFileTextOutline } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorDetail } from '../../lib/apiError'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

/** From a thread: promote it to an application on a listing, a link never a copy (spec 2026-10-25-11). */
export function CreateApplicationButton({ org, conversationUid }: { org: string; conversationUid: string }) {
    const { t } = useTranslation('rentalApplications')
    const navigate = useNavigate()
    const canWrite = useCan('rental_applications.write')
    const [open, setOpen] = useState(false)
    const [listing, setListing] = useState('')
    const [error, setError] = useState<string | null>(null)
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings', { params: { path: { orgCode: org } } }, { enabled: open })
    const promote = $api.useMutation('post', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/rental-application', {
        onSuccess: (res) => navigate({ to: '/$org/rental-applications/$applicationUid', params: { org, applicationUid: res.application.uid } }),
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    if (!canWrite) return null
    const live = (data?.listings ?? []).filter((l) => l.status !== 'withdrawn' && l.status !== 'let')
    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} aria-label={t('promote.button')} title={t('promote.button')} data-testid="conversation-create-application">
                <AppIcon path={mdiAccountFileTextOutline} size="xs" className="md:mr-1" />
                <span className="hidden md:inline">{t('promote.button')}</span>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="conversation-create-application-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('promote.title')}</DialogTitle>
                        <DialogDescription>{t('promote.description')}</DialogDescription>
                    </DialogHeader>
                    <Select value={listing} onValueChange={setListing}>
                        <SelectTrigger data-testid="conversation-create-application-listing"><SelectValue placeholder={t('create.listing_placeholder')} /></SelectTrigger>
                        <SelectContent>
                            {live.map((l) => <SelectItem key={l.uid} value={l.uid}>{l.asset_name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {error && <p className="text-sm text-destructive" data-testid="conversation-create-application-error">{error}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
                        <Button
                            disabled={!listing || promote.isPending}
                            onClick={() => promote.mutate({ params: { path: { orgCode: org, conversationUid } }, body: { listing_uid: listing } })}
                            data-testid="conversation-create-application-confirm"
                        >
                            {t('promote.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
